import { DEFAULT_SCHOOL_ZONE, SCHOOL_ZONES, type SchoolZone } from '@ergo/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { useResource } from '../../hooks';

/**
 * Elle travaille avec des enfants, à l'école comme à domicile : savoir qu'une
 * semaine est en vacances change la façon de la lire. L'app se contente de les
 * montrer — elle n'annule jamais une séance toute seule (DESIGN.md).
 */
export function VacancesScolaires() {
  const settings = useResource(() => api.settings.get(), []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const zone = settings.data?.schoolZone ?? DEFAULT_SCHOOL_ZONE;
  const years = settings.data?.schoolHolidaysYears ?? [];
  const syncError = settings.data?.schoolHolidaysError ?? null;

  async function onChange(next: SchoolZone) {
    setPending(true);
    setError(null);
    try {
      await api.settings.update({ schoolZone: next });
      settings.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Vacances scolaires</h2>

      <label className="field">
        <span className="field__label">Zone</span>
        <select
          className="input"
          value={zone}
          disabled={pending || settings.loading}
          onChange={(event) => void onChange(event.target.value as SchoolZone)}
        >
          {SCHOOL_ZONES.map((choice) => (
            <option key={choice} value={choice}>
              Zone {choice}
            </option>
          ))}
        </select>
        <span className="hint">Toulouse : zone C.</span>
      </label>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* Le calendrier est un décor : un échec se dit, il n'alarme pas. Les dates
          déjà connues restent affichées dans la semaine, c'est là l'essentiel. */}
      <p className={syncError ? 'note-sun' : 'hint'}>
        {syncError
          ? `Calendrier non mis à jour : ${syncError}.${years.length > 0 ? ` Les dates affichées sont celles de ${anneesLabel(years)}.` : ''}`
          : years.length > 0
            ? `Calendrier à jour (${anneesLabel(years)})`
            : 'Calendrier en cours de récupération…'}
      </p>
    </section>
  );
}

/** « année 2026-2027 » · « années 2025-2026 et 2026-2027 » */
function anneesLabel(years: string[]): string {
  if (years.length === 1) return `année ${years[0]}`;
  return `années ${years.slice(0, -1).join(', ')} et ${years.at(-1)}`;
}
